// Four stat tiles for the KPI detail drawer: Current / Target / 12-month
// average / Trend. Shares the MetricCard pattern with the Overview
// summary strip. Trend arrow direction tracks raw value change; colour
// carries good/bad via higherIsBetter.
//
// Behaviour:
//  - Current + 12-month avg values render with the `%` suffix; unit
//    detection still honours the parsed measurement for hrs / ms /
//    rating etc.
//  - Latest-period sub-label under Current so contract managers see
//    which period the headline value actually belongs to.
//  - Current + Target use the shared formatter (Met / Not met, `X hrs`).
//  - 12-month avg tile is suppressed entirely for boolean KPIs — an
//    average has no meaning for Met/Not-met data. The grid falls back
//    to 3 columns on wide screens for boolean KPIs.
//  - Trend tile stays render-agnostic — "—" when numeric deltas aren't
//    computable, which covers boolean naturally.

import type { JSX } from "react";
import type { Kpi, KpiMeasurement } from "../../../../../types";
import { parseKpiMeasurementValue } from "../parseKpiMeasurementValue";
import { formatKpiTarget, formatKpiValue } from "../formatKpiValue";
import {
  formatPeriodLabel,
  normaliseFrequency,
} from "../../../../../lib/kpi/period";

export interface KpiDetailStatsProps {
  kpi: Kpi;
  measurements: KpiMeasurement[];
}

const TILE_CLASS =
  "rounded-md border border-slate-200 bg-white p-3.5 dark:border-slate-700 dark:bg-slate-800/60";
const LABEL_CLASS = "mb-1 text-caption text-slate-500 dark:text-slate-400";
const VALUE_BASE = "text-h2 leading-none text-slate-900 dark:text-slate-100";
const SUB_CLASS = "mt-1 text-caption text-slate-500 dark:text-slate-400";

function sortedByPeriod(ms: KpiMeasurement[]): KpiMeasurement[] {
  return [...ms].sort((a, b) => a.period.localeCompare(b.period));
}

function roundHalfUp(n: number, digits = 0): number {
  const factor = 10 ** digits;
  return Math.round(n * factor) / factor;
}

function formatNumeric(n: number, unit: string): string {
  const display = Number.isInteger(n) ? String(n) : String(roundHalfUp(n, 1));
  // Default to "%" when the parsed unit is empty (percentage is the
  // legacy default display for unit-less KPIs).
  const resolvedUnit = unit || "%";
  return `${display}${resolvedUnit}`;
}

export default function KpiDetailStats({
  kpi,
  measurements,
}: KpiDetailStatsProps): JSX.Element {
  const sorted = sortedByPeriod(measurements);
  const latest = sorted[sorted.length - 1];
  const prior = sorted[sorted.length - 2];
  const valueType = kpi.valueType ?? "percentage";

  // Current — shared formatter handles per-valueType rendering.
  const currentText = formatKpiValue(kpi, latest).display;
  const currentParsed = latest ? parseKpiMeasurementValue(latest.value) : null;

  // 12-month avg — last 12 numeric values. Suppressed entirely for
  // boolean KPIs; the whole tile drops out of the grid below.
  // Boolean KPIs drop both the 12-month avg AND the Trend tiles —
  // neither has a meaningful value for Met/Not-met data. Grid falls
  // back to 2 columns on `md:` (Current + Target). An em-dash tile is
  // safe but wastes a slot, so boolean KPIs suppress it — mirroring
  // how the 12m-avg tile is already handled.
  const showAvgTile = valueType !== "boolean";
  const showTrendTile = valueType !== "boolean";
  const last12 = sorted.slice(-12);
  const numericLast12 = last12
    .map((m) => parseKpiMeasurementValue(m.value).numeric)
    .filter((n): n is number => n !== null);
  const avgText =
    numericLast12.length < 2
      ? "—"
      : formatNumeric(
          numericLast12.reduce((acc, n) => acc + n, 0) / numericLast12.length,
          // Prefer `kpi.unit` for number KPIs so "3.2 hrs" displays
          // consistently with the card hero / log modal.
          valueType === "number" && kpi.unit
            ? kpi.unit
            : (currentParsed?.unit ?? ""),
        );

  // Trend — delta between latest and prior.
  let trendText = "—";
  let trendClass = "text-slate-500 dark:text-slate-400";
  if (latest && prior) {
    const a = parseKpiMeasurementValue(prior.value).numeric;
    const b = parseKpiMeasurementValue(latest.value).numeric;
    if (a !== null && b !== null && a !== b) {
      const delta = b - a;
      const abs = Math.abs(delta);
      const absText = Number.isInteger(abs)
        ? String(abs)
        : String(roundHalfUp(abs, 1));
      const arrow = delta > 0 ? "↑" : "↓";
      const unit = currentParsed?.unit ?? "";
      trendText = unit ? `${arrow} ${absText}${unit}` : `${arrow} ${absText}pt`;
      if (typeof kpi.higherIsBetter === "boolean") {
        const goingUp = delta > 0;
        const isGood = goingUp === kpi.higherIsBetter;
        trendClass = isGood
          ? "text-emerald-600 dark:text-emerald-400"
          : "text-red-600 dark:text-red-400";
      }
    }
  }

  const frequency = normaliseFrequency(
    kpi.measurementFrequency ?? "monthly",
  );
  const latestPeriodLabel = latest
    ? formatPeriodLabel(latest.period, frequency)
    : null;

  return (
    <div
      className={`grid grid-cols-2 gap-3 ${
        // 4 tiles (numeric), 2 tiles (boolean — only Current + Target).
        // No 3-column intermediate — boolean suppresses BOTH avg AND
        // trend together so the grid stays balanced.
        showAvgTile && showTrendTile ? "md:grid-cols-4" : "md:grid-cols-2"
      }`}
    >
      <div className={TILE_CLASS}>
        <div className={LABEL_CLASS}>Current</div>
        <div className={VALUE_BASE} data-testid="kpi-stat-current">
          {currentText}
        </div>
        {/* Name the period the Current value belongs to so "90%"
            isn't ambiguous about which month / quarter it reports. */}
        {latestPeriodLabel && (
          <div className={SUB_CLASS} data-testid="kpi-stat-current-period">
            ({latestPeriodLabel})
          </div>
        )}
      </div>
      <div className={TILE_CLASS}>
        <div className={LABEL_CLASS}>Target</div>
        <div className={VALUE_BASE} data-testid="kpi-stat-target">
          {formatKpiTarget(kpi)}
        </div>
      </div>
      {showAvgTile && (
        <div className={TILE_CLASS}>
          <div className={LABEL_CLASS}>12-month avg</div>
          <div className={VALUE_BASE} data-testid="kpi-stat-avg">
            {avgText}
          </div>
        </div>
      )}
      {showTrendTile && (
        <div className={TILE_CLASS}>
          <div className={LABEL_CLASS}>Trend</div>
          <div
            className={`${VALUE_BASE} ${trendClass}`}
            data-testid="kpi-stat-trend"
          >
            {trendText}
          </div>
        </div>
      )}
    </div>
  );
}
